import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { CustomUnitFieldValue } from '../../models/entities/custom-unit-field-value';
import { Owner } from '../../models/entities/owner';
import { Unit, UnitsResponse } from '../../models/entities/unit';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { GtmService } from '../gtm.service';
import { Units } from '../../components/shared/http/request-interfaces';
import { UnitCustomFieldWithPermissions } from '../../models/entities/UnitCustomFieldWithPermissions';
import { PaymentGroup } from '../../models/entities/payment-group';
import { Charge } from 'app/models/entities/charge';
import { AccountCredit } from '../../models/entities/account-credit';
import environment from '../../../environments/environment';

export interface UnitDeletionStatusResponse {
    deletionId: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    totalUnits: number;
    processedUnits: number;
    chargesProcessed: number;
    createdAt: string;
    errorMessage?: string;
    message?: string;
}

@Injectable({
    providedIn: 'root',
})
export class UnitsService
{
    public deleteUnit$ = new Subject<Unit>();
    public moveInUser$ = new Subject<Unit>();
    public moveOutUser$ = new Subject<{
        owner: Owner,
        unit: Unit
    }>();
    public openNewRecurringChargeModal$ = new Subject<Unit>();
    public recordPayment$ = new Subject<Unit>();
    public openNewChargeModal$ = new Subject<Unit>();
    public showChangeOwnership$ = new Subject<Unit>();
    public reloadUnitList$ = new Subject<boolean>();

    public $units = new BehaviorSubject<Unit[]>([]);

    private _units: Unit[] = [];
    private _unitIdsForMember: number[] = [];


    constructor(
            private _http: ApplicationHttpClient,
            private _gtm: GtmService,
    ) {
        this._units = [];
    }

    isUnitOwner(unitId: number, jwt: JwtLegFiClaims): Observable<boolean> {
        return this.getMemberUnits(jwt.orgId, jwt.memberId)
                .pipe(
                        map((units: Unit[]) => {
                            // Check if the unitId matches either the jwt value, or units belonging to the owner
                            return unitId === jwt.unitId || units.some(unit => unit.id === unitId);
                        }),
                );
    }

    public downloadFile(url: string, contentType: string) {
        const newHeaders = new HttpHeaders({
            'Content-Type': contentType,
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'arraybuffer',
        };

        return this._http.get(url, options);
    }


    public refreshUnitBalance(unitId: number): Observable<number> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnitBalance(jwt.orgId, unitId));

        return this._http.post(url, JSON.stringify({})).pipe(map((res: any) => res.balance));
    }

    /**
     * Return list of all units for an org
     * @param {number} organizationId
     * @param withs
     * @returns {Observable<Object>}
     */
    public getOrganizationUnitsSimple(organizationId: number, withs: string[] = []): Observable<Unit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnitsSimple(organizationId || jwt.orgId) + (withs && withs.length
                ? '?with[]=' + withs.join('&with[]=')
                : ''));

        return this._http.get(url).pipe(map((response: Object[]) => {
            this._units = response
                    .filter((unit) => {
                        return unit !== null;
                    })
                    .map((unit) => {
                        return new Unit(unit);
                    });

            return this._units;
        }));
    }

    /**
     *
     * @param orgId
     * @param search
     * @param sortColumn
     * @param sortDirection
     * @param pageIndex
     * @param perPage
     * @param options
     */
    public getUnits(
            orgId: number,
            search: string = '',
            sortColumn: string = 'name',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: {
                unitTags?: string[],
                withoutUnitTags?: string[],
                ownerTags?: string[],
                withoutOwnerTags?: string[],
                showDeleted?: boolean,
                searchOr?: boolean,
                searchBy?: string[],
                unitIds?: number[],
                weightedVotingCategoryIds?: number[],
                ballotVotingUnitIds?: number[],
                onlyWeightedVotingCategoryOrNull?: number,
                ballotId?: number,
                onlyBallotVotingUnits?: boolean,
                isVotingSearch?: boolean,
                excludeZeroBalanceUnits?: boolean,
                excludeUnitsWithoutOwners?: boolean,
            } = {},
    ): Observable<UnitsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnits(orgId || jwt.orgId));

        const params = [];
        params.push('page=' + pageIndex);
        params.push('column=' + sortColumn);
        params.push('direction=' + sortDirection);
        params.push('perPage=' + perPage);

        if (search !== '') {
            params.push('search=' + encodeURIComponent(search));
        }
        // Tags travel as repeated array params (unitTags[]=A&unitTags[]=B) — a
        // comma-joined string shatters labels that themselves contain a comma (SD-8445)
        if (options.unitTags?.length > 0) {
            options.unitTags.forEach(t => params.push('unitTags[]=' + encodeURIComponent(t)));
        }
        if (options.withoutUnitTags?.length > 0) {
            options.withoutUnitTags.forEach(t => params.push('withoutUnitTags[]=' + encodeURIComponent(t)));
        }
        if (options.ownerTags?.length > 0) {
            options.ownerTags.forEach(t => params.push('ownerTags[]=' + encodeURIComponent(t)));
        }
        if (options.withoutOwnerTags?.length > 0) {
            options.withoutOwnerTags.forEach(t => params.push('withoutOwnerTags[]=' + encodeURIComponent(t)));
        }
        if (options.searchOr) {
            params.push('searchOr=1');
        }
        if (options.showDeleted) {
            params.push('showDeleted=1');
        }
        if (options.searchBy?.length > 0) {
            params.push('searchBy=' + options.searchBy.join(','));
        }
        if (options.unitIds?.length > 0) {
            params.push('unitIds=' + options.searchBy.join(','));
        }
        if (options.weightedVotingCategoryIds?.length > 0) {
            params.push('weightedVotingCategoryIds=' + options.weightedVotingCategoryIds.join(','));
        }
        if ('onlyWeightedVotingCategoryOrNull' in options) {
            params.push('onlyWeightedVotingCategoryOrNull=' + options.onlyWeightedVotingCategoryOrNull);
        }
        if ('ballotId' in options && options.ballotId) {
            params.push('ballotId=' + options.ballotId);
        }
        if ('onlyBallotVotingUnits' in options && options.onlyBallotVotingUnits) {
            params.push('onlyBallotVotingUnits=' + options.onlyBallotVotingUnits);
        }
        if (options.ballotVotingUnitIds?.length > 0) {
            params.push('ballotVotingUnitIds=' + options.ballotVotingUnitIds.join(','));
        }
        if (options.isVotingSearch) {
            params.push('isVotingSearch=1');
        }
        if(options.excludeZeroBalanceUnits) {
            params.push('excludeZeroBalanceUnits=1');
        }
        if(options.excludeUnitsWithoutOwners) {
            params.push('excludeUnitsWithoutOwners=1');
        }
        url = url + '?' + params.join('&');

        return this._http.get(url).pipe(map((resp) => {
            const unitsResponse: UnitsResponse = new UnitsResponse(resp);
            unitsResponse.data.forEach((unit) => {
                this._addUnit(unit);
            });
            this.$units.next(this._units);
            return unitsResponse;
        }));
    }

    /**
     * Get units with owner names for amenity booking typeahead
     * @param {number} orgId - Organization ID
     * @param {string} search - Search term for filtering
     * @returns {Observable<Unit[]>} - Observable of units with displayName format "{Owner Name} • {Unit Name}"
     */
    public getUnitsWithOwners(orgId: number, search: string = ''): Observable<Unit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
            `/api/v2/organizations/${orgId || jwt.orgId}/units/with-owners`
        );

        const params: string[] = [];
        if (search) {
            params.push(`search=${encodeURIComponent(search)}`);
        }

        if (params.length > 0) {
            url += '?' + params.join('&');
        }

        return this._http.get(url).pipe(
            map((response: any) => {
                return (response.data || []).map((item: any) => {
                    const unit = new Unit(item);
                    // Add displayName property for the typeahead
                    (unit as any).displayName = item.displayName;
                    // Add owner/membership IDs for booking creation
                    (unit as any).ownerId = item.ownerId;
                    (unit as any).membershipId = item.membershipId;
                    (unit as any).userId = item.userId;
                    (unit as any).userName = item.userName;
                    (unit as any).userEmail = item.userEmail;
                    return unit;
                });
            })
        );
    }

    /**
     * Get units by array of IDs (for draft restoration)
     * @param {number} orgId - Organization ID
     * @param {number[]} unitIds - Array of unit IDs to fetch
     * @returns {Observable<Unit[]>} - Observable of units with full relations
     */
    public getUnitsByIds(orgId: number, unitIds: number[]): Observable<Unit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.UnitsV2Module.GetUnitsByIds(orgId || jwt.orgId)
        );

        return this._http.post(url, JSON.stringify({ unitIds })).pipe(
            map((response: any) => {
                return (response.data || []).map((item: any) => new Unit(item));
            })
        );
    }

    /**
     * Return list of all units for an org
     * @param {number} orgId
     * @param {string} search
     * @param {string} sortColumn
     * @param {string} sortDirection
     * @param {number} pageIndex
     * @param {number} perPage
     * @param {tags: string[]; unitIds: number[]; columns: string[] } options
     * @returns {Observable<Object>}
     */
    public getOrganizationUnitsListCsv(
            orgId: number,
            search: string = '',
            sortColumn: string = 'name',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: {
                unitTags?: string[],
                withoutUnitTags?: string[],
                ownerTags?: string[],
                withoutOwnerTags?: string[],
                unitIds: number[],
                columns: string[],
                showDeleted?: boolean,
            } = {
                unitIds: [],
                columns: [],
                showDeleted: false,
            },
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnits(orgId || jwt.orgId));
        const params = [];
        params.push('page=' + pageIndex);
        params.push('search=' + encodeURIComponent(search));
        params.push('column=' + sortColumn);
        params.push('direction=' + sortDirection);
        params.push('perPage=' + perPage);
        // Tags travel as repeated array params (unitTags[]=A&unitTags[]=B) — a
        // comma-joined string shatters labels that themselves contain a comma (SD-8445)
        if ('unitTags' in options) {
            options.unitTags.forEach(t => params.push('unitTags[]=' + encodeURIComponent(t)));
        }
        if ('withoutUnitTags' in options) {
            options.withoutUnitTags.forEach(t => params.push('withoutUnitTags[]=' + encodeURIComponent(t)));
        }
        if ('ownerTags' in options) {
            options.ownerTags.forEach(t => params.push('ownerTags[]=' + encodeURIComponent(t)));
        }
        if ('withoutOwnerTags' in options) {
            options.withoutOwnerTags.forEach(t => params.push('withoutOwnerTags[]=' + encodeURIComponent(t)));
        }
        // columns/unitIds are system identifiers that never contain commas — comma-join is safe here
        params.push('columns=' + options.columns.map(t => encodeURIComponent(t)).join(','));
        params.push('unitIds=' + options.unitIds.join(','));
        params.push('showDeleted=' + (options.showDeleted ? 1 : 0));
        params.push('csv=1');

        url = url + '?' + params.join('&');

        return this.downloadFile(url, 'text/csv');
    }

    /**
     * @param {number} organizationId
     * @param {number} memberId
     * @returns {Observable<Unit[]>}
     */
    public getMemberUnits(organizationId: number, memberId: number): Observable<Unit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.MemberUnits(organizationId, memberId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            this._unitIdsForMember = [];
            return response.map((res) => {
                const unit = new Unit(res);
                this._unitIdsForMember.push(unit.id);
                return unit;
            });
        }));
    }

    /**
     * @param {number} id
     * @param {string[]} withs
     * @returns {Observable<Object>}
     */
    public getUnit(id: number, withs: string[] = []): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Unit(id) + (withs && withs.length ? '?with[]=' + withs.join('&with[]=') : ''),
        );

        return this._http.get(url).pipe(map((response: Object) => new Unit(response)));
    }

    /**
     * @param {number} id
     * @param {string[]} withs
     * @returns {Observable<Object>}
     */

    /**
     * Create Unit
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public createUnit(requestBody: Units.CreateUnitRequest): Observable<Unit> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnits(jwt.orgId)),
                JSON.stringify(requestBody),
        ).pipe(
                map((res: Object) => {
                    this._gtm.addUnit({
                        orgId: jwt.orgId,
                        unitName: requestBody.unit.title,
                        addedBy: jwt.id,
                    });
                    return new Unit(res);
                }),
        );
    }

    /**
     * @param {number} orgId
     * @param {number} unitId
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public updateUnit(orgId: number, unitId: number, requestBody: object): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnit(orgId, unitId));

        return this._http.put(url, JSON.stringify(requestBody));
    }

    /**
     * Remove a unit's existing opening (starting) balance so a new one can be set.
     *
     * @param {number} orgId
     * @param {number} unitId
     * @returns {Observable<Object>}
     */
    public removeOpeningBalance(orgId: number, unitId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnitOpeningBalance(orgId, unitId));

        return this._http.delete(url);
    }

    /**
     * @param {number} orgId
     * @param {number} unitId
     * @returns {Observable<Object>}
     */
    public restoreUnit(orgId: number, unitId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.RestoreUnit());
        const requestBody = {orgId: orgId, unitId: unitId};
        return this._http.post(url, JSON.stringify(requestBody));
    }

    /**
     * @param {number} unitId
     * @returns {Observable<Object>}
     */
    public deleteUnit(unitId: number): Observable<Object> {
        return this.deleteUnits([unitId]);
    }

    /**
     * @param {number[]} unitIds
     * @returns {Observable<Object>}
     */
    public deleteUnits(unitIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnits(jwt.orgId) + '?unitIds=' + unitIds.join(',')),
        );
    }

    /**
     * Create a bulk unit deletion request with progress tracking
     * @param {number[]} unitIds
     * @returns {Observable<UnitDeletionStatusResponse>}
     */
    public createBulkDeletion(unitIds: number[]): Observable<UnitDeletionStatusResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
            Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgUnitDeletions(jwt.orgId)),
            JSON.stringify({ unitIds }),
        ).pipe(map((res: any) => res as UnitDeletionStatusResponse));
    }

    /**
     * Get the status of a bulk deletion request (for polling)
     * @param {number} deletionId
     * @returns {Observable<UnitDeletionStatusResponse>}
     */
    public getBulkDeletionStatus(deletionId: number): Observable<UnitDeletionStatusResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
            Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgUnitDeletionStatus(jwt.orgId, deletionId)),
        ).pipe(map((res: any) => res as UnitDeletionStatusResponse));
    }

    /**
     * Cancel a bulk deletion request
     * @param {number} deletionId
     * @returns {Observable<Object>}
     */
    public cancelBulkDeletion(deletionId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.delete(
            Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgUnitDeletionStatus(jwt.orgId, deletionId)),
        );
    }

    /**
     * Retry a stuck or failed bulk deletion request
     * @param {number} deletionId
     * @returns {Observable<Object>}
     */
    public retryBulkDeletion(deletionId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
            Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgUnitDeletionRetry(jwt.orgId, deletionId)),
            JSON.stringify({}),
        );
    }

    /**
     * @param {number} orgId
     * @param {number} unitId
     * @param {number} imageId
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public updateUnitImageCaption(
            orgId: number,
            unitId: number,
            imageId: number,
            requestBody: object,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationUnitImageCaption(orgId, unitId, imageId),
        );

        return this._http.put(url, JSON.stringify(requestBody));
    }

    /**
     * @param {number} unitId
     * @param {number} imageId
     * @returns {Observable<Object>}
     */
    public deleteUnitImage(unitId: number, imageId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationUnitImage(jwt.orgId, unitId, imageId));

        return this._http.delete(url);
    }

    /**
     * Get all images for a unit
     * @param {number} unitId
     * @returns {Observable<any[]>}
     */
    public getUnitImages(unitId: number): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = environment.LegFiCoreApi + '/units/' + unitId + '/images';

        return this._http.get(url).pipe(map((response: any[]) => response));
    }

    /**
     * @param {number} unitId
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public moveMembersIntoUnit(unitId: number, requestBody: object): Observable<Owner[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitMoveMembersIn(unitId));

        return this._http.post(url, JSON.stringify(requestBody))
                .pipe(map((res: Object[]) => {
                    return res.map(owner => new Owner(owner));
                }));
    }

    /**
     * @param {number} unitId
     * @param {{owners:Owner[], forgive:string, pause:boolean}} requestBody
     * @returns {Observable<Object>}
     */
    public moveOwnersOutOfUnit(
            unitId: number,
            requestBody: { owners: number[] },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitMoveMembersOut(unitId)),
                JSON.stringify(requestBody),
        );
    }

    /**
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public bulkImportUnits(requestBody: object): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitBulkImport(jwt.orgId)),
                JSON.stringify(requestBody),
        );
    }

    /**
     * @param {string} batchId
     * @returns {Observable<Object>}
     */
    public getImportResults(batchId: string): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitImportResults(jwt.orgId, batchId));

        return this._http.get(url);
    }

    public getCustomFieldValuesWithPermissions(unitId: number): Observable<UnitCustomFieldWithPermissions[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();

        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Unit(unitId) + '/custom-unit-fields-with-permissions');

        return this._http
                .get(url).pipe(
                        map((response: Object[]) => {
                            return response.map(v => {
                                return new UnitCustomFieldWithPermissions(v);
                            });
                        }));
    }

    public getCustomFieldValues(unitId: number): Observable<CustomUnitFieldValue[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Unit(unitId) + '/custom-fields');

        return this._http
                .get(url).pipe(
                        map((response: Object[]) => {
                            return response.map(v => {
                                return new CustomUnitFieldValue(v);
                            });
                        }));
    }

    /**
     * @param {number} unitId
     * @param {number} customFieldId
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public updateUnitCustomField(
            unitId: number,
            customFieldId: number,
            requestBody: CustomUnitFieldValue,
    ): Observable<CustomUnitFieldValue> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .post(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.Unit(unitId) +
                                '/custom-fields/' +
                                '?customUnitField[]={id:' +
                                customFieldId +
                                '}&value=' +
                                requestBody.value,
                        ),
                        JSON.stringify(requestBody),
                ).pipe(
                        map((res: Response) => {
                            return new CustomUnitFieldValue(res);
                        }));
    }

    /**
     * @param {number} unitId
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public createUnitCustomField(unitId: number, requestBody: CustomUnitFieldValue): Observable<CustomUnitFieldValue> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .post(
                        Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Unit(unitId) + '/custom-fields'),
                        JSON.stringify(requestBody),
                ).pipe(
                        map((res: Response) => {
                            return new CustomUnitFieldValue(res);
                        }));
    }

    /**
     * @param {number} unitId
     * @param {boolean} isCurrentOwnerToggled
     * @returns {Observable<Object>}
     */
    public getUnitAccountHistory(unitId: number, isCurrentOwnerToggled: boolean): Observable<Array<any>> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitAccountHistory(jwt.orgId, unitId, isCurrentOwnerToggled));

        return this._http.get(url).pipe(map((response: {
            data: any,
            date: string,
            type: 'payment' | 'charge' | 'forgiven-charge' | 'credit' | 'returned-payment' | 'refunded-payment',
            amount: number,
            balance: number,
            // SD-6877: present on refund/dispute reversal entries.
            disputed?: boolean,
            originalPaymentDate?: string,
        }[]) => {
            const mapping = {
                'payment': (p) => new PaymentGroup(p),
                'returned-payment': (p) => new PaymentGroup(p),
                'refunded-payment': (p) => new PaymentGroup(p),
                'charge': (p) => new Charge(p),
                'forgiven-charge': (p) => new Charge(p),
                'credit': (p) => new AccountCredit(p),
            };
            return response.map(v => {
                return {
                    ...v,
                    data: mapping[v.type](v.data),
                };
            });
        }));
    }

    /**
     * @param {number} organizationId
     * @param {number} unitId
     * @param {boolean} doIncludeAllOccupancies
     * @returns {Observable<Object>}
     */
    public generateUnitPdf(
            organizationId: number,
            unitId: number,
            doIncludeAllOccupancies: boolean,
    ): Observable<Response> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitPdf(organizationId || jwt.orgId, unitId, doIncludeAllOccupancies));

        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'arraybuffer',
        };

        return this._http.get(url, options);
    }

    /**
     * Get units for a specific user (by userId)
     * @param {number} orgId - Organization ID
     * @param {number} userId - User ID to filter by
     * @returns {Observable<Unit[]>} - Observable of units owned by the user
     */
    public getUnitsForUser(orgId: number, userId: number): Observable<Unit[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            `/api/v2/organizations/${orgId || jwt.orgId}/units/with-owners?userId=${userId}`
        );

        return this._http.get(url).pipe(
            map((response: any) => {
                return (response.data || []).map((item: any) => {
                    const unit = new Unit(item);
                    // Add displayName property for the typeahead
                    (unit as any).displayName = item.displayName;
                    // Add owner/membership IDs for booking creation
                    (unit as any).ownerId = item.ownerId;
                    (unit as any).membershipId = item.membershipId;
                    (unit as any).userId = item.userId;
                    (unit as any).userName = item.userName;
                    (unit as any).userEmail = item.userEmail;
                    return unit;
                });
            })
        );
    }

    public resetStore() {
        this._units = [];
        this.$units.next(this._units);
    }

    private _addUnit(unit: Unit) {
        const index = this._units.findIndex(u => u.id === unit.id);
        if (index !== -1) {
            Object.assign(this._units[index], unit);
        } else {
            this._units.push(unit);
        }
    }
}
